(function () {
  const settings = {
    keyword: 'buy more save more',
    debug: false,
    currencySelectors: '.header__icons [data-currency-converter], .header__icons #currency-convertor, .modal_area [data-currency-converter], .modal_area [data-native-currency-converter], [data-currency-converter], #currency-convertor',
    css: `
      .product-form__item.pb_selectable.boxes .btn[data-wr-volume-injected] {
        display: flex !important;
        flex-direction: column;
        align-items: center;
        gap: 2px;
        height: auto !important;
      }
      .product-form__item.pb_selectable.boxes .wr-column-qty {
        display: block;
        font-weight: 600;
      }
      .product-form__item.pb_selectable.boxes .wr-volume-unit-price {
        display: block;
        color: #79797b;
        font-weight: 400;
        line-height: 16px;
        font-size: 14px;
        white-space: nowrap;
      }
    `
  };

  const QTY_UNIT_PATTERN = /^(\d+)\s*(pc|pack|item|pair)s?\b/i;
  let productDetails = null;
  let activeVariantId = null;
  let isActive = true;

  function isVolumePricingOption(name) {
    return String(name || '').toLowerCase().includes(settings.keyword);
  }

  function extractQtyInfo(optionValue) {
    const match = String(optionValue).trim().match(QTY_UNIT_PATTERN);
    if (!match) return null;
    const qty = parseInt(match[1], 10);
    if (qty < 1) return null;
    const unit = match[2].toLowerCase();
    return {
      quantity: qty,
      qtyLabel: (qty === 1 ? `1 ${unit}` : `${qty} ${unit}s`).toUpperCase(),
      suffix: `/${unit}`,
    };
  }

  // Reconstruct the selected variant id from the swatches actually highlighted
  // in the DOM (Customall's own `.active` state). This is the only source that
  // is guaranteed to be in sync with what the shopper sees right now --
  // `window.ShopifyAnalytics.meta.selectedVariantId` is a Shopify analytics
  // global that Customall updates on its own schedule and can lag behind the
  // visible price/option state by a render cycle.
  function getCurrentVariantIdFromDom() {
    if (!productDetails) return null;
    const root = document.querySelector('.product-attributes') || document;
    const containers = [...root.querySelectorAll('[data-ctm-option-name]')]
      .sort((a, b) => Number(a.dataset.ctmOptionPosition) - Number(b.dataset.ctmOptionPosition));
    if (!containers.length) return null;

    const selectedValues = containers.map(c => {
      const active = c.querySelector('.btn.active[data-ctm-value], .active[data-ctm-value]');
      return active?.getAttribute('data-ctm-value')?.trim().toLowerCase() || null;
    });
    if (selectedValues.some(v => !v)) return null;

    const match = productDetails.variants.find(v =>
      selectedValues.every((val, i) => String(v[`option${i + 1}`] || '').trim().toLowerCase() === val)
    );
    return match ? match.id : null;
  }

  function getCurrentVariantId() {
    const domId = getCurrentVariantIdFromDom();
    if (domId) return domId;
    if (activeVariantId) return activeVariantId;
    const idInput = document.querySelector('form[action*="/cart/add"] [name="id"]');
    if (idInput?.value) return idInput.value;
    return window.ShopifyAnalytics?.meta?.selectedVariantId || window.meta?.product?.selectedVariantId;
  }

  function buildProductDetailsFromAnalytics() {
    const product = window.ShopifyAnalytics?.meta?.product || window.meta?.product;
    if (!product?.variants?.length) return null;

    const root = document.querySelector('.product-attributes') || document;
    const optionNames = [...root.querySelectorAll('[data-ctm-option-name]')]
      .sort((a, b) => Number(a.dataset.ctmOptionPosition) - Number(b.dataset.ctmOptionPosition))
      .map(el => el.dataset.ctmOptionName);
    if (!optionNames.length) return null;

    const variants = product.variants.map(v => {
      const variant = { id: v.id, price: v.price };
      String(v.public_title || '').split(' / ').forEach((part, i) => {
        variant[`option${i + 1}`] = part.trim();
      });
      return variant;
    });

    return { options: optionNames, variants };
  }

  function buildPriceMap() {
    if (!productDetails) return null;
    const currentId = getCurrentVariantId();
    if (!currentId) return null;

    const currentVariant = productDetails.variants.find(v => String(v.id) === String(currentId));
    if (!currentVariant) return null;

    const optionIndex = productDetails.options.findIndex(opt => {
      const name = typeof opt === 'string' ? opt : opt?.name;
      return isVolumePricingOption(name);
    });
    if (optionIndex < 0) { isActive = false; return null; }

    const pricingKey = `option${optionIndex + 1}`;
    let baseKey = null;
    if (productDetails.options.length > 1) {
      const baseIndex = productDetails.options.findIndex(opt => {
        const name = typeof opt === 'string' ? opt : opt?.name;
        return String(name || '').toLowerCase().includes('product');
      });
      if (baseIndex >= 0 && `option${baseIndex + 1}` !== pricingKey) {
        baseKey = `option${baseIndex + 1}`;
      } else if (pricingKey !== 'option1') {
        baseKey = 'option1';
      }
    }

    const bestMatches = new Map();
    for (const variant of productDetails.variants) {
      if (baseKey) {
        if (String(variant[baseKey] || '').trim().toLowerCase() !== String(currentVariant[baseKey] || '').trim().toLowerCase()) continue;
      }
      const optVal = variant[pricingKey];
      if (!optVal) continue;
      let score = 0;
      for (let i = 0; i < productDetails.options.length; i++) {
        const k = `option${i + 1}`;
        if (k !== pricingKey && k !== baseKey) {
          if (String(variant[k] || '').trim().toLowerCase() === String(currentVariant[k] || '').trim().toLowerCase()) score++;
        }
      }
      const existing = bestMatches.get(optVal);
      if (!existing || score > existing.score) bestMatches.set(optVal, { variant, score });
    }

    const priceMap = new Map();
    for (const [optVal, { variant }] of bestMatches) {
      const qtyInfo = extractQtyInfo(optVal);
      if (!qtyInfo) continue;
      const raw = Number(variant.price);
      if (!Number.isFinite(raw) || raw <= 0) continue;
      const totalCents = String(variant.price).includes('.') ? Math.round(raw * 100) : Math.round(raw);
      if (totalCents <= 0) continue;
      priceMap.set(optVal, {
        unitCents: Math.round(totalCents / qtyInfo.quantity),
        qtyLabel: qtyInfo.qtyLabel,
        suffix: qtyInfo.suffix,
      });
    }

    return priceMap.size > 0 ? { priceMap, baseCurrency: window.Shopify?.currency?.active || 'USD' } : null;
  }

  function normalizedLookup(priceMap, key) {
    const norm = String(key || '').trim().toLowerCase();
    for (const [k, v] of priceMap) {
      if (String(k).trim().toLowerCase() === norm) return v;
    }
    return null;
  }

  function formatPrice(cents, currency) {
    if (currency === 'VND') return `${Math.round(cents).toLocaleString('vi-VN')}₫`;
    return `$${(cents / 100).toFixed(2)}`;
  }

  // Remove the redundant ISO currency code (e.g. "USD", "EUR") that the
  // shop's money_format table appends/prepends for some currencies, while
  // leaving real symbols (Rs, kr, SFr, ...) untouched.
  function stripCurrencyCode(text, currencyCode) {
    if (!currencyCode) return text;
    return text.replace(new RegExp('\\s*\\b' + currencyCode + '\\b\\s*', 'g'), ' ').trim();
  }

  // Compute the unit price text for the currently active storefront currency.
  // Always derives from the stable `unitCents` value instead of re-reading
  // the DOM, so repeated calls (e.g. from a MutationObserver) can't
  // accumulate conversions on top of an already-converted price.
  function formatUnitPrice(unitCents, baseCurrency, activeCurrency) {
    if (!activeCurrency || activeCurrency === baseCurrency) {
      return formatPrice(unitCents, baseCurrency);
    }
    if (window.Currency && typeof window.Currency.convert === 'function' && typeof window.Currency.formatMoney === 'function') {
      try {
        const convertedCents = window.Currency.convert(unitCents, baseCurrency, activeCurrency);
        const formatString = (window.Currency.money_format || {})[activeCurrency] || '{{amount}}';
        const formatted = window.Currency.formatMoney(convertedCents, formatString);
        return stripCurrencyCode(formatted, activeCurrency);
      } catch (err) {
        console.error('[WR] Currency conversion failed, falling back to base price', err);
      }
    }
    return formatPrice(unitCents, baseCurrency);
  }

  function applyLabels() {
    if (!isActive) return;
    const config = buildPriceMap();
    if (!config) return;

    const containers = document.querySelectorAll('.product-form__item.pb_selectable.boxes');
    let target = null;
    for (const c of containers) {
      const label = c.querySelector('label');
      if (label && isVolumePricingOption(label.textContent)) { target = c; break; }
    }
    if (!target) return;

    const activeCurrency = window.Currency?.currentCurrency || config.baseCurrency;

    // Pause our own observer while we write, so these writes don't retrigger
    // it and queue a redundant extra 100ms debounce cycle on top of the
    // page-wide currency observers already reacting to the same mutations.
    if (observedContainer) domObserver.disconnect();

    for (const btn of target.querySelectorAll('.buttons-group .btn')) {
      // Store original text before first modification
      if (!btn.dataset.wrOptionValue) {
        btn.dataset.wrOptionValue = btn.textContent.trim();
      }
      const details = normalizedLookup(config.priceMap, btn.dataset.wrOptionValue);
      if (!details) continue;

      // Key on the base-currency amount + active currency, never on the
      // rendered text, so switching currency always triggers a fresh
      // conversion from the stable unitCents value (no compounding).
      const priceKey = `${config.baseCurrency}:${details.unitCents}:${activeCurrency}`;
      const displayPrice = formatUnitPrice(details.unitCents, config.baseCurrency, activeCurrency);

      if (btn.dataset.wrVolumeInjected === '1' && btn.dataset.wrPriceKey === priceKey) {
        continue;
      }

      // Update existing nodes in place rather than replaceChildren()+append()
      // on every call. The theme's currencies.liquid attaches MutationObservers
      // scoped to the whole document that re-run Currency.convertAll on any DOM
      // change; rebuilding every button's full subtree on each currency switch
      // retriggered those page-wide observers right as the shopper clicked a
      // new option, compounding with Customall's own re-render and reading as
      // a laggy/delayed selection. A targeted text/html update avoids that.
      let qtySpan = btn.querySelector(':scope > .wr-column-qty');
      let priceSpan = btn.querySelector(':scope > .wr-volume-unit-price');
      let moneySpan = priceSpan?.querySelector(':scope > .wr-unit-money');

      if (!qtySpan || !priceSpan || !moneySpan) {
        btn.replaceChildren();

        qtySpan = document.createElement('span');
        qtySpan.className = 'wr-column-qty';

        priceSpan = document.createElement('span');
        priceSpan.className = 'wr-volume-unit-price';

        // Deliberately NOT class "money" — the theme's site-wide currency
        // script (currencies.liquid) targets every span.money by default and
        // reformats it with its own "$X.XX USD"-style text (e.g. on classic
        // variant selectCallback), which caused a visible flicker here.
        moneySpan = document.createElement('span');
        moneySpan.className = 'wr-unit-money';

        priceSpan.append(moneySpan, document.createTextNode(details.suffix));
        btn.append(qtySpan, priceSpan);
      }

      if (qtySpan.textContent !== details.qtyLabel) qtySpan.textContent = details.qtyLabel;
      if (moneySpan.innerHTML !== displayPrice) moneySpan.innerHTML = displayPrice;

      btn.dataset.wrVolumeInjected = '1';
      btn.dataset.wrPriceKey = priceKey;
    }

    if (observedContainer) domObserver.observe(observedContainer, OBSERVE_OPTIONS);
  }

  async function loadProduct() {
    try {
      const match = window.location.pathname.match(/\/products\/([^\/]+)/);
      if (match) {
        const res = await fetch(`/products/${match[1]}.js`);
        if (res.ok) productDetails = await res.json();
      }
    } catch (e) {
      console.error('[WR] Failed to load product:', e);
    }
    if (!productDetails) productDetails = buildProductDetailsFromAnalytics();
    if (productDetails) applyLabels();
  }

  // Inject styles
  if (!document.getElementById('customall-volume-pricing-style')) {
    const style = document.createElement('style');
    style.id = 'customall-volume-pricing-style';
    style.textContent = settings.css;
    document.head.appendChild(style);
  }

  // Pre-check: skip entirely if product has no volume pricing option
  (function () {
    const options = window.meta?.product?.options || window._MP_CONFIG_?.product?.options;
    if (options?.length && !options.some(isVolumePricingOption)) isActive = false;
  })();

  if (isActive) loadProduct();

  // Watch .product-attributes for DOM changes (Vue re-renders, lazy mount)
  let domDebounce;
  let observedContainer = null;
  const domObserver = new MutationObserver(() => {
    if (!isActive || !productDetails) return;
    clearTimeout(domDebounce);
    domDebounce = setTimeout(applyLabels, 100);
  });

  const OBSERVE_OPTIONS = { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] };

  function startObserving() {
    const container = document.querySelector('.product-attributes');
    if (container) {
      observedContainer = container;
      domObserver.observe(container, OBSERVE_OPTIONS);
      applyLabels();
    } else {
      const waitObserver = new MutationObserver(() => {
        const c = document.querySelector('.product-attributes');
        if (c) {
          waitObserver.disconnect();
          observedContainer = c;
          domObserver.observe(c, OBSERVE_OPTIONS);
          applyLabels();
        }
      });
      waitObserver.observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startObserving);
  } else {
    startObserving();
  }

  // Variant & currency change handlers
  document.addEventListener('change', (e) => {
    if (!isActive) return;
    if (e.target?.closest('form[action*="/cart/add"]') && e.target.name === 'id') {
      activeVariantId = e.target.value;
      setTimeout(applyLabels, 50);
    }
    if (e.target?.matches(settings.currencySelectors)) {
      setTimeout(applyLabels, 150);
    }
  });

  document.addEventListener('variant:changed', () => {
    if (!isActive) return;
    activeVariantId = null;
    setTimeout(applyLabels, 100);
  });
})();
